// What a supplier sees of an event (Lane 1331, Phase 2 slice 2A).
//
// Pure display pieces with no data loading. The supplier event page uses them,
// and so does the buyer's publish preview, so the buyer checks exactly the
// screen suppliers will read. Everything shown comes from the server's
// supplier views (helpers/sourcing_supplier_views.py); nothing is added here.
import React from 'react';
import './SourcingPortal.css';

export const EVENT_STATUS_TEXT = {
  open: 'Open for quotes', closed: 'Closed', awarded: 'Awarded', cancelled: 'Cancelled',
};

export const OWN_STATUS_TEXT = {
  not_started: 'Not started', viewed: 'Opened', submitted: 'Submitted', declined: 'Declined',
};

// A time in the viewer's own time zone, with the zone name ("Oct 3, 2026,
// 5:00 PM EDT"). Empty text when there is no time or it cannot be read.
// The zone buyer screens show deadlines in (Lane 1442): the open event's own zone,
// set by the buyer workspace while it is open. null (the supplier portal, or an event
// with no zone): the viewer's own zone.
let displayZone = null;
export function setDisplayZone(zone) { displayZone = zone || null; }

export function formatDueAt(iso, zone = displayZone) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const opts = {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    timeZoneName: 'short',
  };
  if (zone) {
    try { return d.toLocaleString(undefined, { ...opts, timeZone: zone }); } catch (e) { /* bad zone: viewer's */ }
  }
  return d.toLocaleString(undefined, opts);
}

// The zone times are shown in, by its long name ("Central Daylight Time"), for the
// event header, so the reader knows which zone every time is in: the same zone
// formatDueAt uses (the viewer's, or the open event's on buyer screens).
export function zoneName(iso, zone = displayZone) {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  try {
    const part = new Intl.DateTimeFormat(undefined, { timeZoneName: 'long', ...(zone ? { timeZone: zone } : {}) }).formatToParts(d)
      .find((x) => x.type === 'timeZoneName');
    return part ? part.value : '';
  } catch (e) {
    return '';
  }
}

// How long until the due time, in words ("6 days", "5 hours", "20 minutes").
export function timeLeftText(iso, nowMs = Date.now()) {
  if (!iso) return 'No due time set';
  const due = new Date(iso).getTime();
  if (!Number.isFinite(due)) return 'No due time set';
  const ms = due - nowMs;
  if (ms <= 0) return 'Due time passed';
  const unit = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return unit(days, 'day', 'days');
  const hours = Math.floor(ms / 3600000);
  if (hours >= 1) return unit(hours, 'hour', 'hours');
  return unit(Math.max(1, Math.floor(ms / 60000)), 'minute', 'minutes');
}

export function formatVolume(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }).format(n);
}

// A unit price at the server's precision in the event currency: at least 2
// decimals, up to 4, so two prices that differ in the 3rd or 4th decimal never
// look the same. The buyer screens use this same function (sourcingApi.js).
export function formatUnitPrice(value, currency) {
  if (value === null || value === undefined || value === '') return 'Not available';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Not available';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: currency || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4,
    }).format(n);
  } catch (e) {
    return `${String(value)} ${currency || ''}`.trim();
  }
}

// Buyer feedback exactly as its supplier reads it (Lane 1331, slice 2D): the
// message, then each line with its part number, the line text, the buyer's
// note and a target price when one is given. The supplier event page and the
// buyer's release review both draw it with this, from the server's supplier
// view, so the buyer checks exactly what the supplier will read.
export function FeedbackBody({ item, currency }) {
  const lines = item.lines || [];
  const hasTarget = (l) => l.target_price !== null && l.target_price !== undefined && l.target_price !== '';
  const showTargets = lines.some(hasTarget);
  const showNotes = lines.some((l) => l.note);
  return (
    <div className="srcp-fb-body">
      {item.message && <div className="srcp-text">{item.message}</div>}
      {lines.length > 0 && (
        <div className="srcp-scroll">
          <table className="srcp-grid">
            <thead>
              <tr>
                <th>Part number</th><th>Comment</th>
                {showTargets && <th className="srcp-r">Target price</th>}
                {showNotes && <th>Note</th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={`${l.part_number}-${i}`}>
                  <td className="srcp-mono">{l.part_number}</td>
                  <td className="srcp-ell srcp-ell--wide" title={l.text || ''}>{l.text || ''}</td>
                  {showTargets && (
                    <td className="srcp-r srcp-num srcp-fb-target">{hasTarget(l) ? formatUnitPrice(l.target_price, currency) : ''}</td>
                  )}
                  {showNotes && <td className="srcp-ell" title={l.note || ''}>{l.note || ''}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const TERM_ROWS = [
  ['currency', 'Currency'],
  ['payment_terms', 'Payment terms'],
  ['incoterms', 'Delivery terms (Incoterms)'],
  ['offer_validity_days', 'Offers valid for'],
  ['delivery_notes', 'Delivery notes'],
  ['other_terms', 'Other terms'],
];

export function EventStatus({ status }) {
  return (
    <span className={`srcp-status srcp-status--${status}`}>{EVENT_STATUS_TEXT[status] || status}</span>
  );
}

// The event header the supplier reads, and the buyer's publish preview of it:
// title and status, then who, which round and the due time in the viewer's
// own zone, with the zone named. `actions` sits at the end of the title row;
// `children` at the end of the meta row.
export function EventHeader({ event, untitled = 'Untitled event', actions = null, children = null, mark = null }) {
  const due = formatDueAt(event.due_at);
  const zone = zoneName(event.due_at);
  return (
    <header className="srcp-head">
      <div className="srcp-titlerow">
        <div className="srcp-titlegrow">
          {mark}
          <h1 className="srcp-h1" title={event.title || ''}>{event.title || <span className="srcp-mut">{untitled}</span>}</h1>
          <EventStatus status={event.status} />
        </div>
        {actions}
      </div>
      <div className="srcp-meta">
        <span>From <span className="srcp-strong">{event.buyer_name}</span></span>
        {event.round && <span>Round <span className="srcp-strong">{event.round}</span></span>}
        <span>{due ? <>Due <span className="srcp-strong">{due}</span></> : 'No due time set'}</span>
        {zone && <span className="srcp-mut">Your time zone: {zone}</span>}
        {children}
      </div>
    </header>
  );
}

export function EventDetails({ event }) {
  const terms = event.terms || {};
  const rows = TERM_ROWS.filter(([key]) => terms[key] !== null && terms[key] !== undefined && terms[key] !== '');
  return (
    <section className="srcp-details">
      <div>
        <h2 className="srcp-h2">About this event</h2>
        {event.description
          ? <div className="srcp-text">{event.description}</div>
          : <div className="srcp-mut">No description.</div>}
      </div>
      <div>
        <h2 className="srcp-h2">Terms</h2>
        {rows.length === 0 && <div className="srcp-mut">No terms given.</div>}
        {rows.length > 0 && (
          <dl className="srcp-terms">
            {rows.map(([key, label]) => (
              <React.Fragment key={key}>
                <dt>{label}</dt>
                <dd>{key === 'offer_validity_days' ? `${terms[key]} days` : String(terms[key])}</dd>
              </React.Fragment>
            ))}
          </dl>
        )}
      </div>
    </section>
  );
}

// `sites` is present only when the buyer shares site names; otherwise the
// buyer's notes come as `notes`. Shared by this table and the bid grid.
export function partsShowSites(items) {
  return items.some((p) => Array.isArray(p.sites));
}
export function buyerNotesOf(part, showSites) {
  return showSites
    ? (part.sites || []).filter((s) => s.note).map((s) => `${s.site}: ${s.note}`)
    : (part.notes || []);
}
// The buyer's notes for a part on one line (a table cell holds one line).
export function buyerNotesText(part, showSites) {
  return buyerNotesOf(part, showSites).join('; ');
}
// The site names of the parts shown, in first-seen order: one volume column
// each, so a cell never holds more than one line.
export function siteColumns(items) {
  const names = [];
  items.forEach((p) => (p.sites || []).forEach((s) => {
    if (!names.includes(s.site)) names.push(s.site);
  }));
  return names;
}
export function siteQuantity(part, site) {
  const rows = (part.sites || []).filter((s) => s.site === site);
  if (rows.length === 0) return '';
  return formatVolume(rows.reduce((sum, s) => sum + (Number(s.quantity) || 0), 0));
}

// The parts as a read-only spreadsheet-style table (the buyer's publish preview).
export function PartsTable({ items }) {
  const showSites = partsShowSites(items);
  const sites = showSites ? siteColumns(items) : [];
  return (
    <div className="srcp-scroll">
      <table className="srcp-grid">
        <thead>
          <tr>
            <th>Part number</th><th>Description</th><th>Unit</th>
            {showSites
              ? sites.map((site) => <th key={site} className="srcp-r">{site} per year</th>)
              : <th className="srcp-r">Annual volume</th>}
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => {
            const notes = buyerNotesText(p, showSites);
            return (
              <tr key={p.part_number}>
                <td className="srcp-mono">{p.part_number}</td>
                <td className="srcp-ell" title={p.description || ''}>{p.description || ''}</td>
                <td>{p.unit || ''}</td>
                {showSites
                  ? sites.map((site) => <td key={site} className="srcp-r srcp-num">{siteQuantity(p, site)}</td>)
                  : <td className="srcp-r srcp-num">{formatVolume(p.volume)}</td>}
                <td className="srcp-ell srcp-notes" title={notes}>{notes}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// ---- Changes the buyer published during a round (slice 2E-1) ----------------------
// One change as the server's supplier view describes it, with the full before
// and after values. The buyer's publish review renders the same component
// from the same server view, so the buyer reads exactly what suppliers read.

function shown(v) {
  return v === null || v === undefined || v === '' ? 'none' : String(v);
}

function BeforeAfter({ label, from, to }) {
  return (
    <div className="srcp-change-pair">
      <span className="srcp-mut">{label}</span>
      <div className="srcp-change-cols">
        <div><span className="srcp-mut">Before</span><div className="srcp-text">{shown(from)}</div></div>
        <div><span className="srcp-mut">After</span><div className="srcp-text">{shown(to)}</div></div>
      </div>
    </div>
  );
}

function sitesText(side) {
  if (!side || !side.sites) return null;
  return side.sites.map((x) => `${x.site}: ${shown(x.quantity)}${x.note ? ` (${x.note})` : ''}`).join('\n');
}

function notesText(side) {
  if (!side || !side.notes) return null;
  return side.notes.join('\n') || null;
}

export function AmendmentChange({ change }) {
  const c = change;
  if (c.kind === 'term') {
    return (
      <li className="srcp-change">
        <BeforeAfter label={c.name || c.term} from={c.from} to={c.to} />
        <div className="srcp-mut">{c.policy_text}</div>
      </li>
    );
  }
  if (c.kind === 'part') {
    const side = c.to || c.from || {};
    const fields = c.fields || [];
    return (
      <li className="srcp-change">
        <div><span className="srcp-mono">{c.part_number}</span> {side.description || ''}</div>
        {c.change !== 'changed' && (
          <div className="srcp-change-pair">
            <span className="srcp-mut">{c.change === 'added' ? 'Now in the event' : 'Was in the event'}</span>
            <div className="srcp-text">
              Volume {shown(side.volume)}{side.unit ? ` ${side.unit}` : ''}
              {sitesText(side) ? `\n${sitesText(side)}` : ''}
              {notesText(side) ? `\nNotes: ${notesText(side)}` : ''}
            </div>
          </div>
        )}
        {c.change === 'changed' && (
          <>
            {fields.includes('quantity') && (
              <BeforeAfter label={`Volume${side.unit ? ` (${side.unit})` : ''}`} from={c.from && c.from.volume} to={c.to && c.to.volume} />
            )}
            {fields.includes('quantity') && (sitesText(c.from) || sitesText(c.to)) && (
              <BeforeAfter label="By site" from={sitesText(c.from)} to={sitesText(c.to)} />
            )}
            {fields.includes('unit') && <BeforeAfter label="Unit" from={c.from && c.from.unit} to={c.to && c.to.unit} />}
            {fields.includes('description') && (
              <BeforeAfter label="Name" from={c.from && c.from.description} to={c.to && c.to.description} />
            )}
            {fields.includes('notes') && (
              <BeforeAfter label="Notes" from={sitesText(c.from) || notesText(c.from)} to={sitesText(c.to) || notesText(c.to)} />
            )}
          </>
        )}
        <div className="srcp-mut">{c.policy_text}</div>
      </li>
    );
  }
  const doc = c.to || c.from || {};
  const what = { added: 'New document', removed: 'Document removed', replaced: 'New version of the document',
    details_changed: 'Document details changed' }[c.change] || 'Document';
  return (
    <li className="srcp-change">
      <div>{what}: {doc.file_name || ''}{c.to && c.to.informational ? ' (for information only)' : ''}</div>
      {c.change === 'details_changed' && c.from && c.to && c.from.file_name !== c.to.file_name && (
        <BeforeAfter label="Name" from={c.from.file_name} to={c.to.file_name} />
      )}
      <div className="srcp-mut">{c.policy_text}</div>
    </li>
  );
}

export function AmendmentChanges({ changes }) {
  return <ul className="srcp-change-list">{(changes || []).map((c, i) => <AmendmentChange key={i} change={c} />)}</ul>;
}
